import {
  BookOpenIcon,
  CalculatorIcon,
  ChartBarIcon,
  CheckSquareIcon,
  GridIcon,
  InstructionalTextIcon,
  MaxDiffIcon,
  NumericIcon,
  RadioButtonIcon,
  RankedIcon,
  VideoIcon,
} from '../../../../../utilities/icons';
import { numeratorFullLogo } from '../../../../../utilities/assets';

export const ASK_WHY_QUOTA_SIZE = 100;
export const ASK_WHY_SURVEY_TYPE_ID = 'ask-why-v3-instant-survey-type';
export const ASK_WHY_SURVEY_TYPE_ID_V4 = 'ask-why-v4-instant-survey-type';

export const AUTH_HEADER = 'insights-authorization';

export const AVAILABLE_PANELISTS_CAP = 10000;

export const DEFAULT_GROUP_NAME = 'Panelist Group';
export const DEFAULT_RESPONSE_COUNT = 200;

export const ERROR_SAVE_DRAFT = 'There was an error while saving changes to your survey.';

export const FIELDS_USING_ID = ['custom group', 'people group', 'purchased'];

export const FOLDER_ID_CUSTOM = 3;
export const FOLDER_ID_RECOMMENDED = 2;

export const IMAGE_UPLOAD_MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
export const instantSurveyGroupRefUrlVariable = 'instant_survey_group_ref';

export const MAINTENANCE_MESSAGE = 'Instant Survey currently down for maintenance.';

export const MODEL_TYPES = {
  QUESTION: 'Question',
  QUESTION_FOLDER: 'QuestionFolder',
};

export const PLACEHOLDER_IMAGE = numeratorFullLogo;

export const PURCHASE_VOLUME_TYPES = {
  ITEM_TOTAL_SPEND: 'item_total_spend',
  ITEM_TOTAL_SPEND_PERCENT: 'item_total_spend_percent',
  TRIP_COUNT: 'trip_count',
  TRIP_COUNT_PERCENT: 'trip_count_percent',
  UNIQUE_BRAND: 'unique_brand',
  UNIQUE_PARENT_BRAND: 'unique_parent_brand',
  UNIT_COUNT: 'unit_count',
  UNIT_COUNT_PERCENT: 'unit_count_percent',
};

export const QUESTION_LOGIC_OPERATORS = {
  ALL: {
    text: 'All of the Following',
    value: 'ALL',
  },
  ANY: {
    text: 'Any of the Following',
    value: 'ANY',
  },
  EQUALS: {
    text: 'Equal to',
    value: 'EQUALS',
  },
  GREATER_THAN: {
    text: 'Greater Than',
    value: 'GREATER_THAN',
  },
  GREATER_THAN_OR_EQUAL: {
    text: 'Greater Than Or Equal',
    value: 'GREATER_THAN_OR_EQUAL',
  },
  LESS_THAN: {
    text: 'Less Than',
    value: 'LESS_THAN',
  },
  LESS_THAN_OR_EQUAL: {
    text: 'Lesser Than Or Equal',
    value: 'LESS_THAN_OR_EQUAL',
  },
  NOT: {
    text: 'Not Any of the Following',
    value: 'NOT',
  },
};

export const QUESTION_TYPES = {
  CHECKBOX: {
    description:
      'Allow respondents to choose more than one option from a list of possible answers.',
    icon: <CheckSquareIcon />,
    name: 'Multiple Select',
    value: 'CHECKBOX',
  },
  CONT_SUM: {
    // external_questions and survey_dashbaord responses use 'CONT_SUM'
    description: 'Ask respondents to distribute a total value across multiple options.',
    icon: <CalculatorIcon />,
    name: 'Continuous Sum',
    value: 'CONT_SUM',
  },
  CONTSUM: {
    description: 'Ask respondents to distribute a total value across multiple options.',
    icon: <CalculatorIcon />,
    name: 'Continuous Sum',
    value: 'CONTSUM',
  },
  ESSAY: {
    description:
      'Give respondents the freedom to answer in their own words, without being limited to predefined options.',
    icon: <BookOpenIcon />,
    name: 'Open End',
    value: 'ESSAY',
  },
  INSTRUCTIONS: {
    description:
      'Add a message or instructions for your survey here. Instructional text does not count as a question.',
    icon: <InstructionalTextIcon />,
    name: 'Instructional Text',
    value: 'INSTRUCTIONS',
  },
  MAX_DIFF: {
    description:
      'Ask respondents to choose the most and least preferred options from a rotating set of items.',
    externalType: 'MAXDIFF',
    icon: <MaxDiffIcon />,
    name: 'MaxDiff',
    value: 'MAX-DIFF',
  },
  NUMERIC: {
    description: 'Ask respondents to enter a number as their answer.',
    externalType: 'TEXTBOX',
    icon: <NumericIcon />,
    name: 'Numeric',
    value: 'TEXT',
  },
  RADIO: {
    description: 'Force respondents to choose only one option from a list of possible answers.',
    icon: <RadioButtonIcon />,
    name: 'Single Select',
    value: 'RADIO',
  },
  // INSRV-2770: Rank questions require 'RANK-DRAGDROP' on creation but are returned from
  // the external_questions endpoint as 'RANK'
  RANK: {
    description: 'Ask respondents to rank options in order of preference or importance.',
    icon: <RankedIcon />,
    name: 'Rank',
    value: 'RANK',
  },
  'RANK-DRAGDROP': {
    description: 'Ask respondents to rank options in order of preference or importance.',
    icon: <RankedIcon />,
    name: 'Rank',
    value: 'RANK-DRAGDROP',
  },
  SEQUENTIAL_MONADIC: {
    description:
      'Ask the same set of questions for each concept, shown to respondents one concept at a time.',
    icon: <GridIcon />,
    name: 'Sequential Monadic',
    value: 'SEQUENTIAL-MONADIC',
  },
  TABLE: {
    description: 'Group similar questions together, each using the same set of answer options.',
    icon: <GridIcon />,
    name: 'Grid',
    value: 'TABLE',
  },
  'TABLE-RADIO': {
    description: 'Group similar questions together, each using the same set of answer options.',
    icon: <GridIcon />,
    name: 'Grid',
    value: 'TABLE-RADIO',
  },
  TURF: {
    description:
      'TURF (Total Unduplicated Reach and Frequency) is a survey-based analysis method that can identify a set of options that when offered together have the largest reach (i.e., appeal to the largest number of unique people).',
    icon: <ChartBarIcon />,
    name: 'TURF',
    value: 'TURF',
  },
  VIDEO: {
    description: 'Ask respondents a question they need to answer with a video.',
    icon: <VideoIcon />,
    name: 'Video',
    value: 'VIDEO',
  },
};

export const QUESTION_TYPES_CAN_CARRY_FORWARD = [
  QUESTION_TYPES.CHECKBOX.value,
  QUESTION_TYPES.CONTSUM.value,
  QUESTION_TYPES.RADIO.value,
  QUESTION_TYPES['RANK-DRAGDROP'].value,
];
export const QUESTION_TYPES_CARRY_FORWARD_SOURCE = [QUESTION_TYPES.CHECKBOX.value];

export const QUESTION_TYPES_CAN_DISQUALIFY = [
  QUESTION_TYPES.CHECKBOX.value,
  QUESTION_TYPES.RADIO.value,
  QUESTION_TYPES.TURF.value,
];
export const QUESTION_TYPES_CAN_RANDOMIZE_OPTIONS = [
  QUESTION_TYPES.CHECKBOX.value,
  QUESTION_TYPES.CONTSUM.value,
  QUESTION_TYPES.RADIO.value,
  QUESTION_TYPES['RANK-DRAGDROP'].value,
  QUESTION_TYPES.TURF.value,
];
export const QUESTION_TYPES_CAN_RANDOMIZE_SUB_QUESTIONS = [QUESTION_TYPES['TABLE-RADIO'].value];
export const QUESTION_TYPES_WITH_CROSS_TABS = [
  QUESTION_TYPES.CHECKBOX.value,
  QUESTION_TYPES.RADIO.value,
  QUESTION_TYPES.RANK.value,
  QUESTION_TYPES['RANK-DRAGDROP'].value, // INSRV-7027: Calypso passes 'RANK-DRAGDROP' as question_type
  QUESTION_TYPES.TABLE.value,
  QUESTION_TYPES['TABLE-RADIO'].value, // INSRV-7223: Calypso passes "TABLE-RADIO" as question_type
];
export const QUESTION_TYPES_WITH_OPTIONS = [
  QUESTION_TYPES.CHECKBOX.value,
  QUESTION_TYPES.CONTSUM.value,
  QUESTION_TYPES.MAX_DIFF.value,
  QUESTION_TYPES.RADIO.value,
  QUESTION_TYPES['RANK-DRAGDROP'].value,
  QUESTION_TYPES.TURF.value,
];
export const QUESTION_TYPES_WITH_SUBQUESTIONS = [QUESTION_TYPES['TABLE-RADIO'].value];
export const QUESTION_TYPES_WITH_MAX_ANSWERS = [
  QUESTION_TYPES.CHECKBOX.value,
  QUESTION_TYPES.TURF.value,
  QUESTION_TYPES['RANK-DRAGDROP'].value,
];

export const QUESTIONS_PER_PAGE = 15;
export const RESEARCH_OBJECTIVES_MAX_LENGTH = 1500;
export const RESPONSE_COUNT_INCREMENT = 50;
export const RESPONSE_COUNT_MINIMUM = 50;

export const SURVEY_DESCRIPTION_DEFAULT = 'Standard Survey (6-15 Questions)';
export const SURVEY_DESCRIPTION_LONG = 'Long Survey (16-20 Questions)';
export const SURVEY_DESCRIPTION_SHORT = 'Short Survey (1-5 Questions)';

export const SURVEY_GROUP_VALIDATION_STATUS = {
  ERROR: 'ERROR',
  NOT_ENOUGH_SAMPLE: 'NOT_ENOUGH_SAMPLE',
  NOT_VALIDATED: 'NOT_VALIDATED', // default
  VALIDATED: 'VALIDATED',
  VALIDATING: 'VALIDATING',
};

export const SURVEY_GROUP_VALIDATION_ERROR_HEADER = 'Complex Sample: Manual Review Required';
export const SURVEY_GROUP_VALIDATION_ERROR_TEXT =
  'The Numerator Research Team will review this quota group for available sample before activating the survey for panelists. If there is a risk of not meeting your desired # of completes, we will contact you.';

export const SURVEY_LENGTH_LONG = 16;
export const SURVEY_LENGTH_SHORT = 5;

export const SURVEY_HELP_URL = 'https://help.insights.numerator.com/en/collections/3625626-surveys';

export const SURVEY_TYPES = {
  MONADIC: 'monadic-concept-test-v3-instant-survey-type',
  NEW_ITEM: 'new-item-v3-instant-survey-type',
  SEQUENTIAL_MONADIC: 'sequential-monadic-concept-test-v3-instant-survey-type',
  TURF: 'turf-v3-instant-survey-type',
  VIDEO: 'video-survey-v3-instant-survey-type',
};

export const CONCEPT_IMAGE_SURVEY_TYPES = [
  'monadic-concept-test-v3-instant-survey-type',
  'new-item-v3-instant-survey-type',
];

export const SURVEY_VALIDATION_ERROR_HEADER = 'Manual Review Required';
export const SURVEY_VALIDATION_ERROR_TEXT =
  'The Numerator Research Team will review this survey for available sample before activating it for panelists. If there is a risk of not meeting your desired # of completes, we will contact you.';

export const TEMPLATES_PER_PAGE = 5;

export const VIDEO_MULTI_GROUP_RESPONSE_COUNT = 25;
export const VIDEO_RESPONSE_COUNT_INCREMENT = 25;
export const VIDEO_SINGLE_GROUP_RESPONSE_COUNT = 50;

export const VIEWABLE_ADDONS = {
  TRANSLATE_SURVEY_FRENCH: {
    displayName: 'Translate Survey French',
    value: 4,
  },
  TRANSLATE_SURVEY_SPANISH: {
    displayName: 'Translate Survey Spanish',
    value: 8,
  },
};
